import { useEffect, useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Download, RefreshCw, Twitter, AlertCircle, Sparkles, Check, Share2, ClipboardCopy } from "lucide-react";
import { API, api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function MarketingPreview({ open, onOpenChange, blueprint, platforms }) {
  const { user } = useAuth();
  const [jobId, setJobId] = useState(null);
  const [slots, setSlots] = useState([]);            // [{name, status, url}]
  const [captions, setCaptions] = useState({});      // {slotName: "headline"}
  const [downloading, setDownloading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);    // public /gallery/:id when shared
  const [savedBpId, setSavedBpId] = useState(null);
  const [bundleCopied, setBundleCopied] = useState(false);
  const pollRef = useRef(null);
  const imgBust = useRef(0);

  // Kick off a new job whenever the dialog opens for a blueprint
  useEffect(() => {
    if (!open || !blueprint) return;
    let cancelled = false;
    setJobId(null); setSlots([]); setCaptions({}); setDownloading(false); setSharing(false); setShareUrl(null); setSavedBpId(null); setBundleCopied(false);

    (async () => {
      try {
        const token = localStorage.getItem("dpa_token");
        const res = await fetch(`${API}/prompt/marketing/start`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ blueprint, platforms, marketing: true }),
        });
        if (res.status === 401) {
          toast("Log in to generate marketing screenshots", {
            description: "This is a paid AI feature — free accounts get 5 prompts a month.",
          });
          onOpenChange(false);
          return;
        }
        if (!res.ok) throw new Error();
        const { job_id, slots } = await res.json();
        if (cancelled) return;
        setJobId(job_id);
        setSlots(slots.map(name => ({ name, status: "pending", url: null })));
      } catch {
        toast.error("Could not start screenshot job");
        onOpenChange(false);
      }
    })();

    return () => { cancelled = true; clearInterval(pollRef.current); };
  }, [open, blueprint, platforms, onOpenChange]);

  // Poll status until all slots settle. Also stops if dialog closes.
  useEffect(() => {
    if (!jobId || !open) return;
    clearInterval(pollRef.current);
    const tick = async () => {
      try {
        const res = await fetch(`${API}/prompt/marketing/status/${jobId}`);
        if (!res.ok) return;
        const { progress } = await res.json();
        setSlots(prev => prev.map(s => ({
          ...s,
          status: progress?.[s.name] ?? s.status,
          url: (progress?.[s.name] === "done")
            ? `${API}/prompt/marketing/image/${jobId}/${s.name}?v=${imgBust.current}`
            : null,
        })));
        if (progress && Object.values(progress).every(v => v !== "pending")) {
          clearInterval(pollRef.current);
        }
      } catch { /* ignore */ }
    };
    tick();
    pollRef.current = setInterval(tick, 2500);
    return () => clearInterval(pollRef.current);
  }, [jobId, open]);

  const regenerate = async (name) => {
    if (!jobId) return;
    imgBust.current += 1;
    setSlots(prev => prev.map(s => s.name === name ? { ...s, status: "pending", url: null } : s));
    try {
      const cap = (captions[name] || "").trim();
      const url = cap
        ? `${API}/prompt/marketing/regenerate/${jobId}/${name}?caption=${encodeURIComponent(cap)}`
        : `${API}/prompt/marketing/regenerate/${jobId}/${name}`;
      await fetch(url, { method: "POST" });
      // restart polling
      clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        const res = await fetch(`${API}/prompt/marketing/status/${jobId}`);
        if (!res.ok) return;
        const { progress } = await res.json();
        setSlots(prev => prev.map(s => ({
          ...s,
          status: progress?.[s.name] ?? s.status,
          url: (progress?.[s.name] === "done")
            ? `${API}/prompt/marketing/image/${jobId}/${s.name}?v=${imgBust.current}`
            : null,
        })));
        if (progress && Object.values(progress).every(v => v !== "pending")) {
          clearInterval(pollRef.current);
        }
      }, 2500);
    } catch {
      toast.error("Regeneration failed");
    }
  };

  const download = async () => {
    if (!jobId) return;
    setDownloading(true);
    try {
      const res = await fetch(`${API}/prompt/marketing/finalize`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ job_id: jobId, blueprint, platforms }),
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(blueprint.title || "MyApp").replace(/\s+/g, "")}-starter.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Starter kit downloaded");
      onOpenChange(false);
    } catch {
      toast.error("Could not download");
    } finally {
      setDownloading(false);
    }
  };

  const shareToAtlas = async () => {
    if (!user) {
      toast("Log in to share to the Atlas", { description: "Signing in lets you publish a public blueprint link." });
      return;
    }
    setSharing(true);
    try {
      let id = savedBpId || blueprint.id;
      if (!id || !blueprint.saved) {
        const saveRes = await api.post("/prompt/blueprints", { blueprint });
        id = saveRes.data.id;
        setSavedBpId(id);
      }
      await api.post(`/prompt/blueprints/${id}/share`, { is_public: true });
      const publicUrl = `${window.location.origin}/gallery/${id}`;
      setShareUrl(publicUrl);
      toast.success("Published to the App Atlas");
    } catch {
      toast.error("Could not share");
    } finally {
      setSharing(false);
    }
  };

  const tweet = () => {
    const title = blueprint?.title || "my app";
    const url = shareUrl || "https://durgaprogrammingadventures.com";
    const text = `Sketched "${title}" — 5 App Store screenshots in one minute with @DurgaAdventures. From prompt to App Store.`;
    const href = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
    window.open(href, "_blank", "noopener,noreferrer");
  };

  const copyBundle = async () => {
    if (!jobId) return;
    try {
      const res = await fetch(`${API}/prompt/marketing/bundle/${jobId}`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      // Make image URLs absolute (backend returns relative if PUBLIC_BASE_URL is unset)
      const apiOrigin = new URL(API, window.location.origin).origin;
      const fixed = {
        ...data,
        screenshots: (data.screenshots || []).map(s => ({
          ...s,
          caption: captions[s.slot] ?? s.caption ?? "",
          image_url: s.image_url
            ? (s.image_url.startsWith("http") ? s.image_url : `${apiOrigin}${s.image_url}`)
            : null,
        })),
      };
      await navigator.clipboard.writeText(JSON.stringify(fixed, null, 2));
      setBundleCopied(true);
      toast.success("Marketing bundle copied to clipboard");
      setTimeout(() => setBundleCopied(false), 1800);
    } catch {
      toast.error("Could not copy bundle");
    }
  };

  const allReady = slots.length > 0 && slots.every(s => s.status !== "pending");
  const anyReady = slots.some(s => s.status === "done");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-cream border-2 border-ink rounded-3xl max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="marketing-preview-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl inline-flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-marigold" /> App Store marketing screenshots
          </DialogTitle>
          <DialogDescription className="text-ink2">
            5 screenshots for <span className="font-semibold text-ink">"{blueprint?.title}"</span> are generating in parallel.
            Click any tile to regenerate that slot. Download when you're happy.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 my-4" data-testid="marketing-slots">
          {slots.map((s, idx) => (
            <div key={s.name} className="relative" data-testid={`marketing-slot-${s.name}`}>
              <div className="aspect-[2/3] rounded-xl overflow-hidden border-2 border-ink bg-paper relative">
                {s.status === "done" && s.url && (
                  <img src={s.url} alt={s.name} className="w-full h-full object-cover" />
                )}
                {s.status === "pending" && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-gradient-to-br from-paper via-saffron/10 to-marigold/10 animate-pulse">
                    <RefreshCw className="w-5 h-5 text-marigold animate-spin" />
                    <span className="text-[11px] text-ink2 font-medium">{idx + 1} / 5</span>
                  </div>
                )}
                {s.status === "failed" && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-red-50">
                    <AlertCircle className="w-5 h-5 text-red-500" />
                    <span className="text-[11px] text-red-600 font-medium">Failed</span>
                  </div>
                )}
              </div>
              <div className="mt-2 flex items-center justify-between">
                <span className="text-xs font-mono text-ink2">{s.name}</span>
                <button
                  onClick={() => regenerate(s.name)}
                  disabled={s.status === "pending"}
                  className="text-xs inline-flex items-center gap-1 text-marigold hover:text-marigold/70 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid={`marketing-regen-${s.name}`}
                  title="Regenerate this screenshot with the caption above"
                >
                  <RefreshCw className="w-3 h-3" /> Regenerate
                </button>
              </div>
              <input
                type="text"
                value={captions[s.name] || ""}
                onChange={(e) => setCaptions(prev => ({ ...prev, [s.name]: e.target.value.slice(0, 60) }))}
                placeholder="Caption (e.g. Track every habit.)"
                maxLength={60}
                className="mt-1.5 w-full text-xs px-2 py-1.5 rounded-lg border border-border bg-paper focus:outline-none focus:border-marigold focus:bg-white transition"
                data-testid={`marketing-caption-${s.name}`}
              />
            </div>
          ))}
        </div>

        <div className="border-t border-border pt-4 mt-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-ink2 inline-flex items-center gap-2">
              {allReady ? (
                <><Check className="w-4 h-4 text-teal" /> All ready</>
              ) : (
                <><RefreshCw className="w-4 h-4 text-marigold animate-spin" /> Generating {slots.filter(s => s.status === "pending").length} more…</>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={copyBundle}
                disabled={!anyReady}
                className="text-sm inline-flex items-center gap-1.5 px-4 py-2 rounded-full border-2 border-ink text-ink hover:bg-ink hover:text-cream transition disabled:opacity-40"
                data-testid="marketing-copy-bundle"
                title="Copy a JSON bundle of captions + image URLs"
              >
                {bundleCopied ? <Check className="w-4 h-4 text-teal" /> : <ClipboardCopy className="w-4 h-4" />}
                {bundleCopied ? "Bundle copied" : "Copy bundle"}
              </button>
              <button
                onClick={shareToAtlas}
                disabled={sharing || !anyReady || !!shareUrl}
                className="text-sm inline-flex items-center gap-1.5 px-4 py-2 rounded-full border-2 border-ink text-ink hover:bg-ink hover:text-cream transition disabled:opacity-40"
                data-testid="marketing-share-atlas"
              >
                <Share2 className="w-4 h-4" />
                {shareUrl ? "Shared to Atlas" : sharing ? "Sharing..." : "Share to Atlas"}
              </button>
              <button
                onClick={tweet}
                disabled={!anyReady}
                className="text-sm inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#1DA1F2] text-white hover:bg-[#1A91DA] transition disabled:opacity-40"
                data-testid="marketing-tweet"
              >
                <Twitter className="w-4 h-4" /> Tweet these
              </button>
              <button
                onClick={download}
                disabled={downloading || !anyReady}
                className="btn-primary text-sm py-2 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="marketing-download"
              >
                <Download className="w-4 h-4" /> {downloading ? "Building..." : "Download .zip"}
              </button>
            </div>
          </div>
          {shareUrl && (
            <div className="mt-3 text-xs text-ink2" data-testid="marketing-share-url">
              Public URL: <a href={shareUrl} target="_blank" rel="noopener noreferrer" className="text-marigold font-medium hover:underline">{shareUrl}</a>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
